import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChallengeActivityQuery,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityService,
  ChallengeActivityStoreModel,
} from '@examdojo/challenge';
import {
  McqAttemptResponseService,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptEntityStoreModel,
} from '@examdojo/question';
import { AssignmentSectionQuestionQuery } from '@examdojo/assignment';
import { catchError, EMPTY, switchMap, of, combineLatest, finalize } from 'rxjs';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { ExamdojoPlatformService } from '../../../shared/platform.service';

@Injectable()
export class SyncChallengeActivityStateService {
  constructor(
    private readonly challengeActivityService: ChallengeActivityService,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly assignmentSectionQuestionQuery: AssignmentSectionQuestionQuery,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly mcqResponseService: McqAttemptResponseService,
  ) {
    const isChallengeEnabled = this.featureFlagService.get(ExamDojoFeatureFlag.Challenge);

    if (isChallengeEnabled) {
      this.syncStoreWhenBackOnline().pipe(takeUntilDestroyed()).subscribe();
    }
  }

  private syncStoreWhenBackOnline() {
    return this.platformService.trigger$.pipe(
      switchMap(() => {
        const localOngoing = this.challengeActivityQuery.getOngoingActivity();

        return this.challengeActivityService.fetchAll().pipe(
          switchMap(() => {
            const remoteOngoing = this.challengeActivityQuery.getOngoingActivity();

            if (!remoteOngoing || !localOngoing || localOngoing.id !== remoteOngoing.id) {
              // different activity will be handled by the separate listener
              return of(null);
            }

            const localQuestionAttempts = this.questionAttemptQuery.getAllEntities();

            if (remoteOngoing.status === 'SUBMITTED') {
              // some of the question attempts might be already in store
              this.questionAttemptEntityService.markAllQuestionAttemptsAsSubmittedLocally();
            }

            return combineLatest([
              this.challengeActivityService.fetchMissingQuestionAttempts(remoteOngoing.id),
              this.shouldUpdateExistingQuestionAttempts(remoteOngoing.id)
                ? this.updateGradingsForExistingQuestionAttempts(localQuestionAttempts)
                : of(true),
              this.syncMcqResponses(),
            ]).pipe(
              finalize(() => {
                if (remoteOngoing.status === 'SUBMITTED') {
                  // fix: it's possible fetch question attempt with pending status, even after grading was triggered for challenge activity
                  this.questionAttemptEntityService.markAllQuestionAttemptsAsSubmittedLocally();
                }
              }),
            );
          }),
          catchError(() => EMPTY),
        );
      }),
    );
  }

  private shouldUpdateExistingQuestionAttempts(challengeActivityId: ChallengeActivityStoreModel['id']): boolean {
    const challengeActivityQuestionAttempts =
      this.challengeActivityQuestionAttemptQuery.getByChallengeActivityId(challengeActivityId);
    const questionAttempts = this.questionAttemptQuery.getAllEntities();
    const assignmentSectionQuestions = this.assignmentSectionQuestionQuery.getAllEntities();
    const allQuestionAttemptsPresent =
      challengeActivityQuestionAttempts.length === questionAttempts.length &&
      assignmentSectionQuestions.length - questionAttempts.length === 0;

    const allQuestionAttemptAlreadyGraded = questionAttempts.every(
      (attemptEntity) => attemptEntity.attempt.status === 'GRADED',
    );

    return !allQuestionAttemptsPresent || !allQuestionAttemptAlreadyGraded;
  }

  private updateGradingsForExistingQuestionAttempts(localQuestionAttempts: QuestionAttemptEntityStoreModel[]) {
    if (localQuestionAttempts.length === 0) {
      return of(null);
    }

    return combineLatest(
      localQuestionAttempts.map((questionAttempt) =>
        this.questionAttemptEntityService.updateQuestionAttemptWithGradings(questionAttempt.id),
      ),
    );
  }

  private syncMcqResponses() {
    const mcqAttemptIds = this.questionAttemptQuery
      .getAllEntities()
      .filter((entity) => this.questionAttemptQuery.isQuestionMultipleChoice(entity.id))
      .map((entity) => entity.id);

    if (mcqAttemptIds.length === 0) {
      return of(null);
    }

    return this.mcqResponseService.fetchByQuestionAttemptIds(mcqAttemptIds);
  }
}
